import { useEffect, useState } from "react";
import { ApiError, fetchPublicMeeting } from "./api/client";
import type { MeetingPublicView } from "./api/types";
import { Ended } from "./screens/Ended";
import { InCall } from "./screens/InCall";
import { PreJoin, type PreJoinChoice } from "./screens/PreJoin";
import { HRDMark } from "./components/HRDMark";
import { Icon, ICON_KEYFRAMES } from "./components/Icons";

type Phase = "loading" | "pre-join" | "in-call" | "ended" | "error";

function parsePath(): { shortCode: string | null; t: string | null } {
  // Route: /v/<short_code>?t=<role-token>
  const url = new URL(window.location.href);
  const parts = url.pathname.split("/").filter(Boolean);
  const idx = parts.indexOf("v");
  const shortCode = idx >= 0 && parts[idx + 1] ? parts[idx + 1] : null;
  const t = url.searchParams.get("t");
  return { shortCode, t };
}

function tabId(): string {
  // Per-tab identity suffix so two tabs of the same role don't collide on
  // LiveKit identity. Survives reloads via sessionStorage.
  const key = "meet_tab_id";
  let val = sessionStorage.getItem(key);
  if (!val) {
    val = Math.random().toString(36).slice(2, 10);
    sessionStorage.setItem(key, val);
  }
  return val;
}

export default function App() {
  const [phase, setPhase] = useState<Phase>("loading");
  const [meeting, setMeeting] = useState<MeetingPublicView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [choice, setChoice] = useState<PreJoinChoice>({
    videoDeviceId: null,
    audioDeviceId: null,
    fx: { blur: false, portrait: false },
  });
  const { shortCode, t } = parsePath();

  useEffect(() => {
    if (!shortCode) {
      setError("Ссылка некорректна — нет идентификатора встречи.");
      setPhase("error");
      return;
    }
    (async () => {
      try {
        const view = await fetchPublicMeeting(shortCode, { t, tab: tabId() });
        setMeeting(view);
        // If the meeting is already ended or expired, skip the room entirely.
        if (view.status === "ended" || view.status === "cancelled") {
          setPhase("ended");
        } else if (view.status === "expired") {
          setError("Срок действия ссылки истёк. Попросите рекрутёра прислать новую.");
          setPhase("error");
        } else {
          setPhase("pre-join");
        }
      } catch (e) {
        if (e instanceof ApiError) {
          if (e.status === 404) setError("Встреча не найдена. Проверьте ссылку.");
          else if (e.status === 410) setError("Ссылка больше не действительна.");
          else if (e.status === 401) setError("Не удалось проверить ссылку — попросите новую.");
          else setError(e.message);
        } else {
          setError("Не удалось загрузить встречу.");
        }
        setPhase("error");
      }
    })();
  }, [shortCode, t]);

  return (
    <>
      <style>{ICON_KEYFRAMES}</style>
      <div className="app-shell">
        {phase === "loading" && <LoadingPanel />}
        {phase === "error" && <ErrorPanel message={error ?? ""} />}
        {phase === "pre-join" && meeting && (
          <PreJoin
            meeting={meeting}
            onJoin={(c) => {
              setChoice(c);
              setPhase("in-call");
            }}
          />
        )}
        {phase === "in-call" && meeting && (
          <InCall
            meeting={meeting}
            roleToken={t}
            choice={choice}
            onLeave={() => setPhase("ended")}
          />
        )}
        {phase === "ended" && meeting && <Ended meeting={meeting} />}
      </div>
    </>
  );
}

function LoadingPanel() {
  return (
    <div className="center-screen">
      <div className="panel" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
        <HRDMark size={28} />
        <div style={{ fontSize: 13, color: "var(--text-2)" }}>Подключаем встречу…</div>
        <Icon name="spinner" size={20} spin />
      </div>
    </div>
  );
}

function ErrorPanel({ message }: { message: string }) {
  return (
    <div className="center-screen">
      <div className="panel">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 10,
          }}
        >
          <HRDMark size={22} />
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            HRDeva · Видеокабинет
          </div>
        </div>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>
          Не получилось подключиться
        </div>
        <div style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.55 }}>
          {message || "Неизвестная ошибка"}
        </div>
      </div>
    </div>
  );
}
